
import { mapGetters, mapState } from 'vuex';
import { formatPercentage } from '@/utils/percentage';
import FIcon from '@/components/atoms/icons/h-icon';
import PlaygroundCta from '@/ui/retirement-calculator/playground/playground-cta.vue';
import breakpointHelper from '@/mixins/breakpoint-helper';
import { YES_NO_OPTIONS } from '@/utils/common-constants';

export default {
  name: 'PensionSolutionTable',

  components: {
    PlaygroundCta,
    FIcon,
  },

  mixins: [breakpointHelper],

  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    showCta: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      rows: [
        { title: this.$t('rows.oneTimeInvestment') },
        { title: this.$t('rows.monthlyInvestment') },
        { title: this.$t('rows.expectedRateOfReturn') },
        { title: this.$t('rows.retirementAge') },
      ],
      selectedTitle: this.$t('recommended'),
      selectedIcon: 'filled-star',
    };
  },

  computed: {
    ...mapGetters({
      solutionsData: 'pension-calculator/pensionSolutionsData',
      normalizedSolutionsData: 'pension-calculator/normalizedSolutionsData',
    }),

    ...mapState({
      canSuggestOwnHome: (state) =>
        state['pension-calculator'].inputs.boolean_home_owner ===
        YES_NO_OPTIONS.no,

      incomeGrowth: (state) =>
        state['pension-calculator'].outputs.assumption_income_growth,
      lifeExpectancy: (state) =>
        state['pension-calculator'].outputs.assumption_life_expectancy,
      retirementAge: (state) =>
        state['pension-calculator'].outputs.suggestion_current_retirement_age,
      /**
       * @returns {PENSION_SOLUTION}
       */
      recommendation: (state) =>
        state['pension-calculator'].outputs.suggestion_recommendation,
      withInflation: (state) => state['pension-calculator'].inputs.inflation,
    }),

    columns() {
      const pppColumn = {
        imageSrc: require('assets/svg/growing-money.svg'),
        title: this.$t('columns.privatePensionPlan'),
      };

      const investmentPropertyColumn = {
        imageSrc: require('assets/svg/apartment.svg'),
        title: this.$t('columns.investmentProperty'),
      };

      const ownHomeColumn = {
        imageSrc: require('assets/svg/big-house.svg'),
        title: this.$t('columns.buyOwnHome'),
      };

      const columns = [pppColumn, investmentPropertyColumn];

      if (this.canSuggestOwnHome) {
        columns.unshift(ownHomeColumn);
      }

      const recommended = columns.find(
        (col) => col.title === this.$t(`columns.${this.recommendation}`),
      );

      return Array.from(new Set([recommended, ...columns]));
    },

    assumptions() {
      return {
        heading: this.$t('assumptions.heading'),
        items: [
          {
            icon: 'person',
            text: this.$t('assumptions.items.1'),
          },
          {
            icon: 'moneybag',
            text: this.$t('assumptions.items.2', {
              value: formatPercentage(this.incomeGrowth * 100),
            }),
          },
          {
            icon: 'increase',
            text: this.$t('assumptions.items.3', {
              value: this.withInflation === YES_NO_OPTIONS.no ? 0 : 2,
            }),
          },
          {
            icon: 'light',
            text: this.$t('assumptions.items.4'),
          },
          {
            icon: 'time-2',
            text: this.$t('assumptions.items.5', {
              value: this.lifeExpectancy,
            }),
          },
        ],
        footer: this.$t('assumptions.footer', {
          value: this.retirementAge,
        }),
      };
    },
  },
};
